import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  CameraAlt as CameraAltIcon,
  CleaningServices as CleaningIcon,
  Image as ImageIcon,
  LocalShipping as ShippingIcon,
  Payments as PaymentsIcon,
  PersonAdd as PersonAddIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Sell as SellIcon,
} from '@mui/icons-material';
import { QRLookupDialog } from './QRLookupDialog';
import {
  ADMIN_INVENTORY_PATH,
  ADMIN_CLEANING_QUEUE_PATH,
  ADMIN_CONSIGNORS_PATH,
  ADMIN_PAYMENTS_PATH,
  ADMIN_PHOTO_QUEUE_PATH,
} from '../routes/routePaths';
import { fetchAdminDashboard } from '../api/adminDashboard';
import type {
  ActivityEvent,
  ActivityType,
  RecentInventoryItem,
  RecentVariantStatus,
} from '../api/adminDashboard';

// ─── Constants ────────────────────────────────────────────────────────────────

const CARD_BORDER = '1px solid';
const CARD_BORDER_COLOR = 'rgba(226, 232, 240, 1)';
const TABLE_HEADER_BG = 'rgba(248, 250, 252, 1)';

const moneyMx = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora mismo';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'ayer';
  return `hace ${days} días`;
}

// ─── Metric card ──────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: number | string;
  icon: React.ReactElement;
  iconBg: string;
  iconColor: string;
  to: string;
  badge?: string;
}

function MetricCard({
  label,
  value,
  icon,
  iconBg,
  iconColor,
  to,
  badge,
}: MetricCardProps): React.ReactElement {
  return (
    <Card
      component={Link}
      to={to}
      elevation={0}
      sx={{
        p: 2.5,
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        borderRadius: 2,
        textDecoration: 'none',
        display: 'block',
        transition: 'transform 0.15s, box-shadow 0.15s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: iconColor,
          }}
        >
          {icon}
        </Box>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: iconBg,
              color: iconColor,
              border: 'none',
            }}
          />
        )}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block' }}
      >
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ mt: 0.25, color: '#111827' }}>
        {value}
      </Typography>
    </Card>
  );
}

MetricCard.defaultProps = { badge: undefined };

// ─── Variant status chip ──────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RecentVariantStatus, { label: string; bg: string; color: string }> = {
  photo_queue: { label: 'Cola de fotos', bg: '#FFFBEB', color: '#B45309' },
  cleaning_queue: { label: 'Cola de limpieza', bg: '#EFF6FF', color: '#1D4ED8' },
  active: { label: 'Activo', bg: '#F0FDF4', color: '#15803D' },
  sold: { label: 'Vendido', bg: '#F5F3FF', color: '#6D28D9' },
  inactive: { label: 'Inactivo', bg: '#F9FAFB', color: '#6B7280' },
};

function VariantStatusChip({ status }: { status: RecentVariantStatus }): React.ReactElement {
  const { label, bg, color } = STATUS_CONFIG[status];
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.03em',
        bgcolor: bg,
        color,
        border: '1px solid',
        borderColor: `${color}33`,
      }}
    />
  );
}

// ─── Recent inventory table ───────────────────────────────────────────────────

function RecentInventoryTable({ items }: { items: RecentInventoryItem[] }): React.ReactElement {
  return (
    <Paper
      elevation={0}
      sx={{
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: CARD_BORDER,
          borderColor: CARD_BORDER_COLOR,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Últimas adiciones al inventario
        </Typography>
        <Typography
          component={Link}
          to={ADMIN_INVENTORY_PATH}
          variant="caption"
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            fontWeight: 600,
            '&:hover': { color: '#111827' },
          }}
        >
          Ver todo →
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: TABLE_HEADER_BG }}>
              <TableCell
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', minWidth: 260 }}
              >
                Artículo
              </TableCell>
              <TableCell
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', minWidth: 160 }}
              >
                Consignador
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 140 }}
              >
                Precio
              </TableCell>
              <TableCell
                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', width: 160 }}
              >
                Estado
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.variantId} hover>
                <TableCell sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: 'rgba(248,250,252,1)',
                        border: CARD_BORDER,
                        borderColor: CARD_BORDER_COLOR,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {item.imageUrl ? (
                        <Box
                          component="img"
                          src={item.imageUrl}
                          alt={item.productName}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.25 }}
                        />
                      ) : (
                        <ImageIcon sx={{ color: 'action.disabled', fontSize: 18 }} />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {item.productName || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.sizeName || '—'}
                        {item.artId ? ` · UU${item.artId}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {item.consignorName || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {item.publicPrice != null ? moneyMx(item.publicPrice) : '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>
                  <VariantStatusChip status={item.status} />
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.disabled">
                    Sin artículos recientes
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ─── Activity feed ────────────────────────────────────────────────────────────

const ACTIVITY_CONFIG: Record<ActivityType, { icon: React.ReactElement; bg: string; to: string }> =
  {
    item_received: {
      icon: <ShippingIcon sx={{ fontSize: 16 }} />,
      bg: '#111827',
      to: ADMIN_INVENTORY_PATH,
    },
    sale_completed: {
      icon: <SellIcon sx={{ fontSize: 16 }} />,
      bg: '#15803D',
      to: ADMIN_INVENTORY_PATH,
    },
    payment_processed: {
      icon: <PaymentsIcon sx={{ fontSize: 16 }} />,
      bg: '#1D4ED8',
      to: ADMIN_PAYMENTS_PATH,
    },
    new_consignor: {
      icon: <PersonAddIcon sx={{ fontSize: 16 }} />,
      bg: '#6D28D9',
      to: ADMIN_CONSIGNORS_PATH,
    },
  };

function ActivityFeed({ events }: { events: ActivityEvent[] }): React.ReactElement {
  return (
    <Paper
      elevation={0}
      sx={{
        border: CARD_BORDER,
        borderColor: CARD_BORDER_COLOR,
        borderRadius: 2,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2.5 }}>
        Actividad reciente
      </Typography>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {events.map((event) => {
          const cfg = ACTIVITY_CONFIG[event.type];
          return (
            <Box
              key={event.id}
              component={Link}
              to={cfg.to}
              sx={{
                display: 'flex',
                gap: 1.5,
                textDecoration: 'none',
                '&:hover .activity-desc': { color: '#111827' },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: cfg.bg,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {cfg.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  className="activity-desc"
                  variant="body2"
                  color="text.primary"
                  sx={{ transition: 'color 0.15s', lineHeight: 1.4 }}
                >
                  {event.description}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {relativeTime(event.createdAt)}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">
              Sin actividad reciente
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminHomePage(): React.ReactElement {
  const navigate = useNavigate();
  const [qrLookupOpen, setQrLookupOpen] = useState(false);

  const handleQRFound = (variantId: number) => {
    // Navigate to inventory page with the variant pre-selected via query param
    navigate(`${ADMIN_INVENTORY_PATH}?qrVariantId=${variantId}`);
  };

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    pendingPhotos: 0,
    pendingCleaning: 0,
    paymentsScheduledToday: 0,
    unscheduledPaymentsCount: 0,
    newConsignorsThisWeek: 0,
  });
  const [recentInventory, setRecentInventory] = useState<RecentInventoryItem[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityEvent[]>([]);

  useEffect(() => {
    setLoading(true);
    fetchAdminDashboard()
      .then((result) => {
        setLoading(false);
        if (!result.ok) {
          setLoadError(result.error);
          return;
        }
        setMetrics(result.data.metrics);
        setRecentInventory(result.data.recentInventory);
        setRecentActivity(result.data.recentActivity);
      })
      .catch(() => {
        setLoading(false);
        setLoadError('No se pudo cargar el dashboard.');
      });
  }, []);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: '-0.02em', textAlign: 'left' }}
        >
          Resumen del día
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, textAlign: 'left' }}>
          Bienvenido. Esto es lo que está pasando en Urban Utility Guadalajara.
        </Typography>
      </Box>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress sx={{ color: '#111827' }} />
        </Box>
      )}

      {/* QR scan FAB */}
      <Tooltip title="Escanear etiqueta QR">
        <Fab
          aria-label="Escanear QR"
          onClick={() => setQrLookupOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 32,
            bgcolor: '#111827',
            color: '#fff',
            '&:hover': { bgcolor: '#1e293b' },
            zIndex: 1200,
          }}
        >
          <QrCodeScannerIcon />
        </Fab>
      </Tooltip>

      <QRLookupDialog
        open={qrLookupOpen}
        onClose={() => setQrLookupOpen(false)}
        onFound={handleQRFound}
      />

      {!loading && (
        <>
          {/* Metric cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 4,
            }}
          >
            <MetricCard
              label="Fotos pendientes"
              value={metrics.pendingPhotos}
              icon={<CameraAltIcon fontSize="small" />}
              iconBg="#EFF6FF"
              iconColor="#3B82F6"
              to={ADMIN_PHOTO_QUEUE_PATH}
              badge={metrics.pendingPhotos > 0 ? 'Atención' : undefined}
            />
            <MetricCard
              label="Por limpiar"
              value={metrics.pendingCleaning}
              icon={<CleaningIcon fontSize="small" />}
              iconBg="#FFFBEB"
              iconColor="#B45309"
              to={ADMIN_CLEANING_QUEUE_PATH}
            />
            <MetricCard
              label="Pagos programados hoy"
              value={metrics.paymentsScheduledToday}
              icon={<WalletIcon fontSize="small" />}
              iconBg="#F0FDF4"
              iconColor="#22C55E"
              to={`${ADMIN_PAYMENTS_PATH}?filter=dueTodayOrOverdue`}
            />
            <MetricCard
              label="Nuevos consignadores"
              value={metrics.newConsignorsThisWeek}
              icon={<PersonAddIcon fontSize="small" />}
              iconBg="#F5F3FF"
              iconColor="#7C3AED"
              to={ADMIN_CONSIGNORS_PATH}
              badge={metrics.newConsignorsThisWeek > 0 ? 'Esta semana' : undefined}
            />
          </Box>

          {/* Second row */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 3,
            }}
          >
            <RecentInventoryTable items={recentInventory} />
            <ActivityFeed events={recentActivity} />
          </Box>
        </>
      )}
    </Box>
  );
}
